import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useModerationActions } from '@/moderation-actions/api'
import { MODERATION_ACTION_VALUES, type ModerationActionType } from '@/moderation-actions/schemas'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'

const ACTION_LABEL_KEY: Record<ModerationActionType, string> = {
  approve_photo: 'moderationActions.actionApprovePhoto',
  reject_photo: 'moderationActions.actionRejectPhoto',
  disable_user: 'moderationActions.actionDisableUser',
  reinstate_user: 'moderationActions.actionReinstateUser',
  dismiss_report: 'moderationActions.actionDismissReport',
  resolve_report: 'moderationActions.actionResolveReport',
  delete_message: 'moderationActions.actionDeleteMessage',
}

function formatIsoDate(value: string | null | undefined): string {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString('hu-HU')
  } catch {
    return '—'
  }
}

/**
 * `/moderation-actions` — admin audit-trail lista (DEC-ADMIN-005).
 *
 * Read-only nézet: minden admin-state-flip (terminal report-flip,
 * user-status flip, photo moderation, message-soft-delete) egy
 * `moderation_actions` row-t hagy. Filter: `action` enum (7 érték)
 * + Mind. A `target_type` és `admin_user_id` filterek post-MVP.
 */
export function ModerationActionList() {
  const { t } = useTranslation()
  const [actionFilter, setActionFilter] = useState<ModerationActionType | undefined>(undefined)
  const actions = useModerationActions({ action: actionFilter })

  return (
    <main className="mx-auto flex max-w-3xl flex-col gap-6 p-6">
      <header className="flex flex-col gap-1">
        <Link to="/" className="text-sm text-primary underline-offset-4 hover:underline self-start">
          {t('moderationActions.back')}
        </Link>
        <h1 className="text-2xl font-bold tracking-tight">{t('moderationActions.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('moderationActions.intro')}</p>
      </header>

      <nav className="flex flex-wrap gap-2" aria-label={t('moderationActions.actionLabel')}>
        <Button
          type="button"
          variant={actionFilter === undefined ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActionFilter(undefined)}
        >
          {t('moderationActions.filterAll')}
        </Button>
        {MODERATION_ACTION_VALUES.map((value) => (
          <Button
            key={value}
            type="button"
            variant={actionFilter === value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActionFilter(value)}
          >
            {t(ACTION_LABEL_KEY[value])}
          </Button>
        ))}
      </nav>

      {actions.isPending && <p className="text-muted-foreground">{t('common.loading')}</p>}

      {actions.isError && (
        <Alert variant="destructive">
          <AlertDescription>{t('moderationActions.loadError')}</AlertDescription>
        </Alert>
      )}

      {!actions.isPending && !actions.isError && (actions.data?.length ?? 0) === 0 && (
        <p className="text-muted-foreground">{t('moderationActions.empty')}</p>
      )}

      {!actions.isPending && !actions.isError && (actions.data?.length ?? 0) > 0 && (
        <ul className="flex flex-col gap-3">
          {(actions.data ?? []).map((action, index) => (
            <li
              key={action.id ?? `idx-${index}`}
              className="flex flex-col gap-1 rounded-lg border p-3"
            >
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
                <span className="font-medium">#{action.id ?? '—'}</span>
                <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-medium">
                  {action.action != null
                    ? t(ACTION_LABEL_KEY[action.action as ModerationActionType])
                    : '—'}
                </span>
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                  {`${action.target_type ?? '—'} #${action.target_id ?? '—'}`}
                </span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {formatIsoDate(action.created_at)}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('moderationActions.adminLabel')}: #{action.admin_user_id ?? '—'}
                {action.reason && (
                  <span className="ml-2">
                    · {t('moderationActions.reasonLabel')}: {action.reason}
                  </span>
                )}
              </p>
            </li>
          ))}
        </ul>
      )}
    </main>
  )
}
